<template>
  <div class="std-page">
    <div style="text-align: center;">
      <h5>查询所有学生信息</h5>
      <h6>总共有{{ totalCount }}人，未查询有{{ unqueriedCount }}人，已查询有{{ queriedCount }}人</h6>
    </div>

    <div class="toolbar">
      <template v-if="mode === 'pregen'">
        <el-button type="warning" @click="startPregen">
          预生成（未生成 {{ pregenMissing }} 人）
        </el-button>
        <el-button type="danger" plain @click="clearAllPregen">清除所有预生成</el-button>
      </template>
      <el-button
        type="primary"
        :disabled="selectedStudents.length === 0"
        @click="batchDownload(selectedStudents)"
      >
        下载选中（{{ selectedStudents.length }}）
      </el-button>
      <el-button
        type="success"
        :disabled="students.length === 0"
        @click="batchDownload(students)"
      >
        下载全部（{{ students.length }}）
      </el-button>
    </div>

    <el-alert
      v-if="mode === 'pregen'"
      class="mode-alert"
      type="warning"
      :closable="false"
      show-icon
      title="当前为预生成模式：查询/预览优先使用缓存图片。导入新学生后请点击“预生成”补齐。"
    />

    <el-table
      ref="tableRef"
      :data="students"
      style="width: 100%"
      @selection-change="handleSelectionChange"
    >
      <el-table-column type="selection" width="55" />
      <el-table-column prop="name" label="名字" width="180" />
      <el-table-column prop="std_idcard_num" label="身份证号码" width="180" />
      <el-table-column prop="preregno" label="预报名号" width="180" />
      <el-table-column prop="find_number" label="被查询次数" width="180" />
      <el-table-column label="操作" width="300">
        <template #default="scope">
          <el-button size="small" @click="previewImage(scope.row)">预览</el-button>
          <el-button size="small" type="primary" @click="downloadSingle(scope.row)">下载</el-button>
          <template v-if="mode === 'pregen'">
            <el-button
              v-if="!isCached(scope.row)"
              size="small"
              type="warning"
              @click="pregenOne(scope.row)"
            >预生成</el-button>
            <el-button
              v-else
              size="small"
              type="danger"
              plain
              @click="deleteOne(scope.row)"
            >删除预生成</el-button>
          </template>
        </template>
      </el-table-column>
    </el-table>

    <!-- 预览弹窗 -->
    <el-dialog v-model="previewVisible" title="录取通知书预览" width="60%" align-center>
      <div class="preview-body">
        <el-image
          v-if="previewUrl"
          :src="previewUrl"
          fit="contain"
          style="max-width: 100%"
        />
        <div v-else class="preview-loading">正在生成预览...</div>
      </div>
      <template #footer>
        <el-button @click="previewVisible = false">关闭</el-button>
        <el-button type="primary" :disabled="!previewRow" @click="downloadSingle(previewRow)">
          下载此张
        </el-button>
      </template>
    </el-dialog>

    <!-- 批量生成进度弹窗 -->
    <el-dialog
      v-model="batchVisible"
      title="正在生成录取通知书"
      width="420px"
      align-center
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
    >
      <div class="batch-body">
        <el-progress
          type="circle"
          :percentage="batchPercentage"
          :status="batchStatusText === '打包下载中...' ? 'success' : ''"
        />
        <p class="batch-text">
          已生成 <b>{{ batchGenerated }}</b> 人，剩余 <b>{{ batchRemaining }}</b> 人
        </p>
        <p class="batch-status">{{ batchStatusText }}</p>
      </div>
    </el-dialog>

    <!-- 预生成进度弹窗 -->
    <el-dialog
      v-model="pregenVisible"
      title="正在预生成录取通知书"
      width="420px"
      align-center
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
    >
      <div class="batch-body">
        <el-progress type="circle" :percentage="pregenPercentage" />
        <p class="batch-text">
          已处理 <b>{{ pregenGenerated }}</b> 人，剩余 <b>{{ pregenRemaining }}</b> 人
        </p>
        <p class="batch-warn">请不要关闭此窗口，直到预生成完成。</p>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import { ElMessage, ElMessageBox } from 'element-plus';

export default {
  data() {
    return {
      students: [],
      selectedStudents: [],
      // 预览
      previewVisible: false,
      previewUrl: '',
      previewRow: null,
      // 批量
      batchVisible: false,
      batchGenerated: 0,
      batchTotal: 0,
      batchStatusText: '',
      pollTimer: null,
      batchPolling: false,
      // 生成模式 / 预生成
      mode: 'realtime',
      cachedIds: [],
      pregenVisible: false,
      pregenGenerated: 0,
      pregenTotal: 0,
      pregenTimer: null,
      pregenPolling: false,
    };
  },
  mounted() {
    this.fetchStudents();
    this.fetchMode();
  },
  beforeUnmount() {
    this.clearPoll();
    this.clearPregenPoll();
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
  },
  computed: {
    totalCount() {
      return this.students.length;
    },
    unqueriedCount() {
      return this.students.filter((s) => s.find_number === 0).length;
    },
    queriedCount() {
      return this.students.filter((s) => s.find_number > 0).length;
    },
    cachedSet() {
      return new Set(this.cachedIds);
    },
    pregenMissing() {
      return Math.max(this.students.length - this.cachedIds.length, 0);
    },
    batchRemaining() {
      return Math.max(this.batchTotal - this.batchGenerated, 0);
    },
    batchPercentage() {
      if (!this.batchTotal) return 0;
      return Math.round((this.batchGenerated / this.batchTotal) * 100);
    },
    pregenRemaining() {
      return Math.max(this.pregenTotal - this.pregenGenerated, 0);
    },
    pregenPercentage() {
      if (!this.pregenTotal) return 0;
      return Math.round((this.pregenGenerated / this.pregenTotal) * 100);
    },
  },
  methods: {
    async fetchStudents() {
      try {
        const response = await axios.get('/students');
        this.students = response.data;
      } catch (error) {
        console.error('获取学生信息失败:', error);
        ElMessage.error('获取学生信息失败');
      }
    },
    handleSelectionChange(selection) {
      this.selectedStudents = selection;
    },
    async previewImage(row) {
      this.previewRow = row;
      this.previewVisible = true;
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = '';
      }
      try {
        const res = await axios.post(
          '/api/preview-image',
          { name: row.name, std_idcard_num: row.std_idcard_num },
          { responseType: 'blob' }
        );
        this.previewUrl = URL.createObjectURL(res.data);
      } catch (error) {
        console.error('预览失败:', error);
        ElMessage.error('生成预览失败，请检查模板是否已配置');
        this.previewVisible = false;
      }
    },
    async downloadSingle(row) {
      if (!row) return;
      try {
        const res = await axios.post(
          '/api/preview-image',
          { name: row.name, std_idcard_num: row.std_idcard_num },
          { responseType: 'blob' }
        );
        this.saveBlob(res.data, `${row.name}.jpg`);
      } catch (error) {
        console.error('下载失败:', error);
        ElMessage.error('生成图片失败，请检查模板是否已配置');
      }
    },
    async fetchMode() {
      try {
        const res = await axios.get('/api/gen-mode');
        this.mode = res.data.mode;
        if (this.mode === 'pregen') this.fetchCached();
      } catch (e) {
        this.mode = 'realtime';
      }
    },
    async fetchCached() {
      try {
        const res = await axios.get('/api/pregen/cached');
        this.cachedIds = res.data.cached || [];
      } catch (e) {
        this.cachedIds = [];
      }
    },
    isCached(row) {
      return this.cachedSet.has(row.std_idcard_num);
    },
    async startPregen() {
      let jobId = '';
      try {
        const res = await axios.post('/api/pregen/start');
        jobId = res.data.job_id;
        this.pregenTotal = res.data.total;
        this.pregenGenerated = 0;
        this.pregenVisible = true;
      } catch (error) {
        ElMessage.warning(error.response?.data?.error || '启动预生成失败');
        return;
      }
      this.pollPregen(jobId);
    },
    pollPregen(jobId) {
      this.clearPregenPoll();
      this.pregenTimer = setInterval(async () => {
        if (this.pregenPolling) return;
        this.pregenPolling = true;
        try {
          const res = await axios.get(`/api/pregen/progress/${jobId}`);
          const data = res.data;
          this.pregenGenerated = data.generated;
          this.pregenTotal = data.total;
          if (data.error) {
            this.clearPregenPoll();
            this.pregenVisible = false;
            ElMessage.error(`预生成失败：${data.error}`);
            return;
          }
          if (data.done) {
            this.clearPregenPoll();
            this.pregenVisible = false;
            ElMessage.success('预生成完成');
            this.fetchCached();
          }
        } catch (error) {
          this.clearPregenPoll();
          this.pregenVisible = false;
          ElMessage.error('查询预生成进度失败');
        } finally {
          this.pregenPolling = false;
        }
      }, 800);
    },
    clearPregenPoll() {
      if (this.pregenTimer) {
        clearInterval(this.pregenTimer);
        this.pregenTimer = null;
      }
    },
    async clearAllPregen() {
      try {
        await ElMessageBox.confirm(
          '将删除所有预生成的通知书图片，删除后需要重新预生成。确定继续吗？',
          '清除所有预生成',
          { type: 'warning', confirmButtonText: '确定删除', cancelButtonText: '取消' }
        );
      } catch (e) {
        return;
      }
      try {
        const res = await axios.post('/api/pregen/clear');
        ElMessage.success(`已删除 ${res.data.removed ?? 0} 张预生成图片`);
        this.cachedIds = [];
      } catch (error) {
        ElMessage.error('清除失败');
      }
    },
    async pregenOne(row) {
      try {
        await axios.post('/api/pregen/one', {
          name: row.name,
          std_idcard_num: row.std_idcard_num,
        });
        ElMessage.success(`已预生成 ${row.name} 的通知书`);
        if (!this.cachedIds.includes(row.std_idcard_num)) {
          this.cachedIds = [...this.cachedIds, row.std_idcard_num];
        }
      } catch (error) {
        ElMessage.error(error.response?.data?.error || '预生成失败');
      }
    },
    async deleteOne(row) {
      try {
        const res = await axios.post('/api/pregen/delete-one', {
          std_idcard_num: row.std_idcard_num,
        });
        if (res.data.existed) {
          ElMessage.success(`已删除 ${row.name} 的预生成图片`);
        } else {
          ElMessage.info(`${row.name} 尚未预生成`);
        }
        this.cachedIds = this.cachedIds.filter((id) => id !== row.std_idcard_num);
      } catch (error) {
        ElMessage.error('删除失败');
      }
    },
    async batchDownload(list) {
      const names = list.map((s) => s.name).filter(Boolean);
      if (names.length === 0) {
        ElMessage.warning('没有可下载的学生');
        return;
      }

      this.batchGenerated = 0;
      this.batchTotal = names.length;
      this.batchStatusText = '正在生成中...';
      this.batchVisible = true;

      let jobId = '';
      try {
        const res = await axios.post('/api/batch-generate', { names });
        jobId = res.data.job_id;
        this.batchTotal = res.data.total;
      } catch (error) {
        console.error('启动批量生成失败:', error);
        ElMessage.error('启动批量生成失败');
        this.batchVisible = false;
        return;
      }

      this.pollProgress(jobId);
    },
    pollProgress(jobId) {
      this.clearPoll();
      this.pollTimer = setInterval(async () => {
        if (this.batchPolling) return; // 避免请求堆叠（大批量时轮询重入会拖垮单线程服务）
        this.batchPolling = true;
        try {
          const res = await axios.get(`/api/batch-progress/${jobId}`);
          const data = res.data;
          this.batchGenerated = data.generated;
          this.batchTotal = data.total;

          if (data.error) {
            this.clearPoll();
            this.batchVisible = false;
            ElMessage.error(`生成失败：${data.error}`);
            return;
          }

          if (data.done) {
            this.clearPoll();
            this.batchStatusText = '打包下载中...';
            await this.downloadZip(jobId);
            this.batchVisible = false;
          }
        } catch (error) {
          this.clearPoll();
          this.batchVisible = false;
          console.error('查询进度失败:', error);
          ElMessage.error('查询生成进度失败');
        } finally {
          this.batchPolling = false;
        }
      }, 800);
    },
    async downloadZip(jobId) {
      try {
        const res = await axios.get(`/api/batch-download/${jobId}`, {
          responseType: 'blob',
        });
        this.saveBlob(res.data, '录取通知书.zip');
        ElMessage.success('下载完成');
      } catch (error) {
        let msg = '下载压缩包失败';
        try {
          const data = error.response?.data;
          if (data && data.type === 'application/json') {
            const obj = JSON.parse(await data.text());
            if (obj.error) msg += `：${obj.error}`;
          }
        } catch (e) {
          /* ignore parse error */
        }
        console.error('下载压缩包失败:', error);
        ElMessage.error(msg);
      }
    },
    saveBlob(blob, filename) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    clearPoll() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
  },
};
</script>

<style scoped>
.std-page {
  max-width: 1000px;
  margin: 0 auto;
  padding: 16px;
}
.toolbar {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: 12px;
}
.preview-body {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  max-height: 70vh;
  overflow-y: auto;
}
.preview-loading {
  color: #909399;
  padding: 40px 0;
}
.batch-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
}
.batch-text {
  font-size: 15px;
  color: #303133;
  margin: 0;
}
.batch-text b {
  color: #409eff;
}
.batch-status {
  color: #909399;
  margin: 0;
  font-size: 13px;
}
.batch-warn {
  color: #e6a23c;
  margin: 0;
  font-size: 13px;
  font-weight: 600;
}
.mode-alert {
  margin-bottom: 12px;
}
</style>